/**
 * ce-v2-launch.js — Shared v2 Customizer launch helper
 *
 * Exposes:
 *   window.ceBuildCustomizerLaunchUrl(input)  → URL string
 *   window.ceNavigateToCustomizer(input)      → navigates browser
 */
(function () {
  'use strict';

  var MARKETING_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'
  ];

  /*
   * Ad theme (customized repo, lib/customizer/ad-theme.ts): the customizer
   * leads its Designs with the theme of the ad the shopper clicked. A link's
   * own ?theme= wins; otherwise the theme is read from the ad name every
   * Meta ad already sends as utm_content (CE-WK2641-CLASSICCAR-VID-MONTAGE ->
   * classic-cars). Nothing found = no theme, the customizer as today. Inert
   * until NEXT_PUBLIC_CUSTOMIZER_AD_THEME is on in the app.
   */
  var THEME_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  var AD_NAME_THEMES = [
    [['CLASSICCAR', 'CLASSICCARS', 'CAR', 'CARS', 'GARAGE'], 'classic-cars'],
    [['NEWBORN', 'BABY'], 'new-baby'],
    [['DOG', 'DOGS', 'PUPPY', 'PUP'], 'dogs'],
    [['CAT', 'CATS', 'KITTEN'], 'cats'],
    [['COUPLE', 'COUPLES', 'WEDDING', 'ANNIVERSARY', 'VALENTINE', 'VALENTINES'], 'couples'],
    [['BESTIE', 'BESTIES', 'FRIEND', 'FRIENDS', 'BFF'], 'best-friends'],
    [['FAMILY', 'MOM', 'DAD', 'GRANDMA', 'GRANDPA'], 'family']
  ];

  function adThemeFromName(name) {
    if (!name) return null;
    var tokens = String(name).toUpperCase().split(/[^A-Z0-9]+/);
    /* The full cell table (snippets/ce-ad-cell-heroes.liquid, present only
       while the theme setting "Ad cell heroes" is on, phone case pages): the
       first cell word in the name wins, so CE-WK2643-HORSE-UGC opens on the
       horse designs and a memorial ad (MEMORIAL-DOG) opens on no theme. */
    var cells = window.__ceCellHeroes;
    if (cells && cells.t && cells.m) {
      var has = Object.prototype.hasOwnProperty;
      for (var k = 0; k < tokens.length; k++) {
        if (has.call(cells.t, tokens[k])) {
          var cell = cells.t[tokens[k]];
          return has.call(cells.m, cell) ? cells.m[cell] : null;
        }
      }
    }
    for (var i = 0; i < AD_NAME_THEMES.length; i++) {
      var words = AD_NAME_THEMES[i][0];
      for (var j = 0; j < words.length; j++) {
        if (tokens.indexOf(words[j]) !== -1) return AD_NAME_THEMES[i][1];
      }
    }
    return null;
  }

  function getAdTheme(sp) {
    var own = (sp.get('theme') || '').trim().toLowerCase();
    if (own && own.length <= 40 && THEME_SLUG_RE.test(own)) return own;
    return adThemeFromName(sp.get('utm_content'));
  }

  function getMarketingParams() {
    var params = [];
    try {
      var sp = new URLSearchParams(window.location.search);
      for (var i = 0; i < MARKETING_PARAMS.length; i++) {
        var key = MARKETING_PARAMS[i];
        if (sp.has(key)) {
          params.push(encodeURIComponent(key) + '=' + encodeURIComponent(sp.get(key)));
        }
      }
      var theme = getAdTheme(sp);
      if (theme) params.push('theme=' + encodeURIComponent(theme));
    } catch (e) { /* old browsers */ }
    return params;
  }

  /**
   * @param {Object} input
   * @param {string}  input.launchBase        - v2 launch base URL
   * @param {string}  input.shopDomain        - shop.permanent_domain
   * @param {number|null} input.variantId     - resolved variant ID
   * @param {number|null} input.productId     - product ID
   * @param {string}  input.productHandle     - product handle
   * @param {string|null} input.productSpecSlug - customized.product_spec_slug
   * @param {boolean} input.useLegacy         - ce_rebrand.use_legacy_customizer
   * @param {number|null} [input.packSize]    - pack-first buy box selection
   *   (ce-pack-picker); rides to the editor as `pack=` so the post-save
   *   quantity offer can pre-set its stepper. Omitted for singles. When
   *   the caller leaves it undefined (e.g. the sticky bar CTA), the
   *   picker's global `window.ceSelectedPackSize` is used so every CTA
   *   on the page carries the same pack.
   * @param {boolean} [input.packFullAdd]     - cart pack-slots lever
   *   (Phase 1b): the picker sets `window.ceSelectedPackFullAdd` from the
   *   product's pack_picker metafield (fullAdd:true). Sends `pack_add=1`
   *   so the customizer stages the FULL pack at add-to-cart.
   * @param {boolean} [input.packBirthSingle] - P6 birth mode: the picker
   *   sets `window.ceSelectedPackBirthSingle` from the metafield
   *   (birthMode:"single"). Sends `pack_birth=1` INSTEAD of `pack_add=1`:
   *   a normal single-unit add that still stamps pack identity, so the
   *   cart renders the set with the remaining slots open. An app build
   *   that predates the param treats it as a plain single add (safe).
   * @param {boolean} [input.rosterIntent]   - group order (2026-09-18): the
   *   PDP's "Ordering for a group?" row (ce-group-order) sets
   *   `window.ceRosterIntent`; sends `roster=1` so the customizer opens the
   *   size roster after the first design. Never sent alongside a pack
   *   (packs are identical copies; the roster is one design across
   *   sizes). An app build that predates the param ignores it (safe).
   * @returns {string} URL
   */
  function buildUrl(input) {
    /* Legacy path */
    if (input.useLegacy) {
      var legacyParams = ['legacy=1'];
      if (input.variantId) {
        legacyParams.push('variant_id=' + encodeURIComponent(input.variantId));
      } else if (input.productId) {
        legacyParams.push('product_id=' + encodeURIComponent(input.productId));
      }
      return '/pages/customize?' + legacyParams.join('&');
    }

    /* v2 path */
    var base = input.launchBase || 'https://app.customenvy.com/customize/launch';
    var parts = [];

    parts.push('shop=' + encodeURIComponent(input.shopDomain || ''));

    if (input.variantId) {
      parts.push('variant_id=' + encodeURIComponent(input.variantId));
    }
    if (input.productId) {
      parts.push('product_id=' + encodeURIComponent(input.productId));
    }
    if (input.productHandle) {
      parts.push('product_handle=' + encodeURIComponent(input.productHandle));
    }
    if (input.productSpecSlug) {
      parts.push('slug=' + encodeURIComponent(input.productSpecSlug));
      parts.push('handle_fallback=0');
    } else {
      parts.push('handle_fallback=1');
    }

    var packSize = input.packSize !== undefined ? input.packSize : window.ceSelectedPackSize;
    var packFullAdd = input.packFullAdd !== undefined ? input.packFullAdd : window.ceSelectedPackFullAdd;
    var packBirthSingle = input.packBirthSingle !== undefined ? input.packBirthSingle : window.ceSelectedPackBirthSingle;
    if (packSize && packSize > 1) {
      parts.push('pack=' + encodeURIComponent(packSize));
      if (packBirthSingle === true) {
        /* P6 single-unit birth wins over the full-add lever. */
        parts.push('pack_birth=1');
      } else if (packFullAdd === true) {
        parts.push('pack_add=1');
      }
    } else {
      var rosterIntent = input.rosterIntent !== undefined ? input.rosterIntent : window.ceRosterIntent;
      if (rosterIntent === true) {
        parts.push('roster=1');
      }
    }

    /* CRO Test #3: the PDP offer-card test arm (ce-product-hero sets
       window.ceOfferArm to "control" or "A" while the test is armed) rides
       along as po= so design starts can be read per arm first-party. An app
       build that predates the param ignores it (safe). */
    if (window.ceOfferArm) {
      parts.push('po=' + encodeURIComponent(window.ceOfferArm));
    }

    parts.push('storefront_url=' + encodeURIComponent(window.location.href));

    /* Marketing params passthrough */
    var mktg = getMarketingParams();
    for (var i = 0; i < mktg.length; i++) {
      parts.push(mktg[i]);
    }

    var sep = base.indexOf('?') === -1 ? '?' : '&';
    return base + sep + parts.join('&');
  }

  function navigate(input) {
    window.location.href = buildUrl(input);
  }

  window.ceBuildCustomizerLaunchUrl = buildUrl;
  window.ceNavigateToCustomizer = navigate;
})();
